import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ShopMap from './components/ShopMap';
import ShopList from './components/ShopList';
import ShopFilters from './components/ShopFilters';
import ShopDetailModal from './components/ShopDetailModal';

const LocalShopFinder = () => {
  const navigate = useNavigate();
  const [userLocation, setUserLocation] = useState(null);
  const [selectedShop, setSelectedShop] = useState(null);
  const [searchRadius, setSearchRadius] = useState(10);
  const [filters, setFilters] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailShop, setDetailShop] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);
  const [mobileActiveView, setMobileActiveView] = useState('map'); // 'map' or 'list'

  // Mock shop data
  const mockShops = [
    {
      id: 1,
      name: "Cycle Central",
      address: "123 Main St, New York, NY 10001",
      coordinates: { lat: 40.7589, lng: -73.9851 },
      phone: "(555) 123-4567",
      website: "https://cyclecentral.com",
      rating: 4.8,
      reviewCount: 127,
      isPartner: true,
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400",
      services: ["sales", "repair", "maintenance", "wheel-building"],
      brands: ["Trek", "Specialized", "Giant"],
      specialties: ["Road Bikes", "Mountain Bikes", "Custom Builds"],
      hours: {
        monday: "9:00-18:00",
        tuesday: "9:00-18:00", 
        wednesday: "9:00-18:00",
        thursday: "9:00-19:00",
        friday: "9:00-19:00",
        saturday: "8:00-17:00",
        sunday: "10:00-16:00"
      },
      description: `Cycle Central has been serving NYC cyclists for over 20 years. We specialize in high-performance road and mountain bikes, offering expert repairs and custom builds.`,
      images: [
        "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800",
        "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=800"
      ],
      serviceDetails: [
        {
          name: "Basic Tune-Up",
          price: "$75",
          description: "Complete bike inspection, brake and derailleur adjustment, chain cleaning",
          duration: "1-2 hours"
        },
        {
          name: "Wheel Building",
          price: "$120-200",
          description: "Custom wheel builds with premium components",
          duration: "2-3 days"
        }
      ],
      recentReviews: [
        {
          id: 1,
          author: "Mike Johnson",
          rating: 5,
          date: "2 weeks ago",
          comment: "Excellent service! They fixed my derailleur issue quickly and the price was fair."
        },
        {
          id: 2,
          author: "Sarah Chen",
          rating: 4,
          date: "1 month ago", 
          comment: "Great selection of bikes and knowledgeable staff. Highly recommend for serious cyclists."
        }
      ]
    },
    {
      id: 2,
      name: "Brooklyn Bike Works",
      address: "456 Atlantic Ave, Brooklyn, NY 11217",
      coordinates: { lat: 40.6892, lng: -73.9442 },
      phone: "(555) 234-5678",
      website: "https://brooklynbikeworks.com",
      rating: 4.6,
      reviewCount: 89,
      isPartner: false,
      image: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=400",
      services: ["repair", "maintenance", "accessories", "rental"],
      brands: ["Cannondale", "Scott", "Bianchi"],
      specialties: ["Urban Commuting", "Vintage Restoration"],
      hours: {
        monday: "10:00-19:00",
        tuesday: "10:00-19:00",
        wednesday: "10:00-19:00", 
        thursday: "10:00-19:00",
        friday: "10:00-19:00",
        saturday: "9:00-18:00",
        sunday: "closed"
      }
    },
    {
      id: 3,
      name: "Manhattan Cycling",
      address: "789 Broadway, New York, NY 10003",
      coordinates: { lat: 40.7505, lng: -73.9934 },
      phone: "(555) 345-6789",
      rating: 4.4,
      reviewCount: 156,
      isPartner: true,
      image: "https://images.unsplash.com/photo-1502744688674-c619d1586c9e?w=400",
      services: ["sales", "repair", "fitting", "suspension"],
      brands: ["Cervelo", "Pinarello", "Specialized"],
      specialties: ["Performance Bikes", "Professional Fitting"],
      hours: {
        monday: "8:00-20:00",
        tuesday: "8:00-20:00",
        wednesday: "8:00-20:00",
        thursday: "8:00-20:00", 
        friday: "8:00-20:00",
        saturday: "8:00-19:00",
        sunday: "9:00-17:00"
      }
    },
    {
      id: 4,
      name: "Queens Bike Hub",
      address: "321 Northern Blvd, Queens, NY 11354",
      coordinates: { lat: 40.7614, lng: -73.8370 },
      phone: "(555) 456-7890",
      rating: 4.2,
      reviewCount: 73,
      isPartner: false,
      services: ["sales", "repair", "accessories"],
      brands: ["Giant", "Trek", "Cannondale"],
      specialties: ["Family Bikes", "E-Bikes"],
      hours: {
        monday: "9:00-18:00",
        tuesday: "9:00-18:00",
        wednesday: "9:00-18:00",
        thursday: "9:00-18:00",
        friday: "9:00-18:00",
        saturday: "9:00-17:00",
        sunday: "10:00-16:00"
      }
    },
    {
      id: 5,
      name: "Bronx Bicycle Co",
      address: "654 Grand Concourse, Bronx, NY 10451",
      coordinates: { lat: 40.8176, lng: -73.9182 },
      phone: "(555) 567-8901",
      rating: 4.7,
      reviewCount: 94,
      isPartner: true,
      services: ["repair", "maintenance", "wheel-building", "fitting"],
      brands: ["Specialized", "Trek", "Scott"],
      specialties: ["Mountain Bikes", "BMX"],
      hours: {
        monday: "10:00-19:00",
        tuesday: "10:00-19:00",
        wednesday: "10:00-19:00",
        thursday: "10:00-19:00",
        friday: "10:00-19:00",
        saturday: "9:00-18:00",
        sunday: "11:00-17:00"
      }
    }
  ];

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation?.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position?.coords?.latitude,
            lng: position?.coords?.longitude
          });
          setIsLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          // Default to NYC coordinates
          setUserLocation({ lat: 40.7128, lng: -74.0060 });
          setIsLoading(false);
        }
      );
    } else {
      setUserLocation({ lat: 40.7128, lng: -74.0060 });
      setIsLoading(false);
    }

    // Check for mobile view
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 1024);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => window.removeEventListener('resize', checkMobileView);
  }, [navigate]);

  const handleShopSelect = (shop) => {
    setSelectedShop(shop);
  };

  const handleGetDirections = (shop) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${shop?.coordinates?.lat},${shop?.coordinates?.lng}`;
    window.open(url, '_blank');
  };

  const handleCallShop = (shop) => {
    window.location.href = `tel:${shop?.phone}`;
  };

  const handleViewDetails = (shop) => {
    setDetailShop(shop);
    setIsDetailModalOpen(true);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pt-20">
        <div className="flex items-center justify-center h-96">
          <div className="flex items-center space-x-3">
            <Icon name="Loader2" size={24} className="animate-spin" color="var(--color-accent)" />
            <span className="text-muted-foreground">Finding bike shops near you...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="px-6 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Find Local Bike Shops
              </h1>
              <p className="text-muted-foreground">
                Discover nearby bike shops for professional installation and parts
              </p>
            </div>
            
            {/* Desktop Filter Toggle */}
            <div className="hidden lg:block">
              <ShopFilters
                isOpen={isFiltersOpen}
                onClose={() => setIsFiltersOpen(false)}
                onFiltersChange={handleFiltersChange}
                initialFilters={filters}
              />
            </div>
          </div>

          {/* Mobile View Toggle */}
          {isMobileView && (
            <div className="flex space-x-2 mb-4">
              <Button
                variant={mobileActiveView === 'map' ? 'default' : 'outline'}
                size="sm"
                iconName="Map"
                iconPosition="left"
                onClick={() => setMobileActiveView('map')}
                className="flex-1"
              >
                Map View
              </Button>
              <Button
                variant={mobileActiveView === 'list' ? 'default' : 'outline'}
                size="sm"
                iconName="List"
                iconPosition="left"
                onClick={() => setMobileActiveView('list')}
                className="flex-1"
              >
                List View
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Filter"
                onClick={() => setIsFiltersOpen(true)}
              >
                Filter
              </Button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex gap-6 h-[calc(100vh-200px)]">
          {/* Desktop Layout */}
          {!isMobileView && (
            <>
              {/* Map Section */}
              <div className="flex-1">
                <ShopMap
                  shops={mockShops}
                  selectedShop={selectedShop}
                  onShopSelect={handleShopSelect}
                  userLocation={userLocation}
                  searchRadius={searchRadius}
                  onRadiusChange={setSearchRadius}
                  filters={filters}
                />
              </div>

              {/* Shop List Section */}
              <div className="w-96">
                <ShopList
                  shops={mockShops}
                  selectedShop={selectedShop}
                  onShopSelect={handleShopSelect}
                  onGetDirections={handleGetDirections}
                  onCallShop={handleCallShop}
                  onViewDetails={handleViewDetails}
                  userLocation={userLocation}
                  filters={filters}
                />
              </div>
            </>
          )}

          {/* Mobile Layout */}
          {isMobileView && (
            <div className="flex-1">
              {mobileActiveView === 'map' ? (
                <ShopMap
                  shops={mockShops}
                  selectedShop={selectedShop}
                  onShopSelect={handleShopSelect}
                  userLocation={userLocation}
                  searchRadius={searchRadius}
                  onRadiusChange={setSearchRadius}
                  filters={filters}
                />
              ) : (
                <ShopList
                  shops={mockShops}
                  selectedShop={selectedShop}
                  onShopSelect={handleShopSelect}
                  onGetDirections={handleGetDirections}
                  onCallShop={handleCallShop}
                  onViewDetails={handleViewDetails}
                  userLocation={userLocation}
                  filters={filters}
                />
              )}
            </div>
          )}
        </div>

        {/* Mobile Selected Shop Quick Actions */}
        {isMobileView && selectedShop && (
          <div className="fixed bottom-6 left-6 right-6 bg-card border border-border rounded-lg p-4 shadow-lg animate-slide-up">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-foreground">{selectedShop?.name}</h3>
                <p className="text-sm text-muted-foreground">{selectedShop?.address}</p>
              </div>
              <button
                onClick={() => setSelectedShop(null)}
                className="p-1 hover:bg-muted rounded-full transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Navigation"
                iconPosition="left"
                onClick={() => handleGetDirections(selectedShop)}
                className="flex-1"
              >
                Directions
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Phone"
                iconPosition="left"
                onClick={() => handleCallShop(selectedShop)}
                className="flex-1"
              >
                Call
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Info"
                onClick={() => handleViewDetails(selectedShop)}
              >
                Details
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Filters Modal */}
      <ShopFilters
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        onFiltersChange={handleFiltersChange}
        initialFilters={filters}
      />
      {/* Shop Detail Modal */}
      <ShopDetailModal
        shop={detailShop}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setDetailShop(null);
        }}
        onGetDirections={handleGetDirections}
        onCallShop={handleCallShop}
        userLocation={userLocation}
      />
    </div>
  );
};

export default LocalShopFinder;